import { isLocale, type Locale, localeFromLanguages } from "../../shared/locale";

export type { Locale } from "../../shared/locale";

const KEY = "clerk.locale.v1";
/** Survives the reload a language switch does, so the player lands back on the menu instead of the attract screen. */
const RESUME_KEY = "clerk.locale.resume";

function stored(): Locale | null {
  try {
    const v = localStorage.getItem(KEY);
    return isLocale(v) ? v : null;
  } catch {
    return null;
  }
}

/** `?lang=en` / `?lang=zh` (shareable links, and the fallback when storage is blocked). */
function fromUrl(): Locale | null {
  if (typeof location === "undefined") return null;
  const v = new URLSearchParams(location.search).get("lang")?.toLowerCase();
  if (!v) return null;
  return v.startsWith("zh") ? "zh-Hant" : v === "en" || v.startsWith("en-") ? "en" : null;
}

function detected(): Locale {
  if (typeof navigator === "undefined") return "zh-Hant";
  const langs = navigator.languages.length ? navigator.languages : [navigator.language];
  return localeFromLanguages(langs);
}

/**
 * The language of this page load. Fixed for the lifetime of the page: level
 * modules resolve their text when they are imported, so switching reloads.
 */
export const locale: Locale = fromUrl() ?? stored() ?? detected();
export const isEn = locale === "en";

/** Save the choice and reload into it (straight back to the menu). */
export function switchLocale(next: Locale): void {
  if (next === locale) return;
  const url = new URL(location.href);
  url.searchParams.delete("lang");
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // Storage blocked: carry the choice in the URL instead.
    url.searchParams.set("lang", next === "en" ? "en" : "zh");
  }
  try {
    sessionStorage.setItem(RESUME_KEY, "menu");
  } catch {
    /* lands on the attract screen instead; fine */
  }
  location.href = url.href;
}

/** True once, right after a language switch reloaded the page. */
export function resumingFromSwitch(): boolean {
  try {
    const v = sessionStorage.getItem(RESUME_KEY);
    sessionStorage.removeItem(RESUME_KEY);
    return v === "menu";
  } catch {
    return false;
  }
}

/**
 * Inline bilingual text for game content (levels, cast, prompts): both
 * editions sit side by side, and leaving one out fails typecheck.
 * Works for strings, arrays and objects of the same shape.
 */
export function tr(zh: string, en: string): string;
export function tr<T>(zh: T, en: NoInfer<T>): T;
export function tr<T>(zh: T, en: T): T {
  return isEn ? en : zh;
}
